/**
 * TagPicker — popover cu toate tagurile tenantului, toggle per tag.
 * Folosit în VaultDetail + NoteEditor pentru attach/detach tag-uri.
 */
import { useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { tagsApi } from '@/api/tags';
import TagPill from '@/components/ui/TagPill';

interface Props {
  tenantId: string;
  selected: Set<string>;
  onChange: (next: Set<string>) => void;
}

export default function TagPicker({ tenantId, selected, onChange }: Props) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const list = useQuery({
    queryKey: ['tags', tenantId],
    queryFn: () => tagsApi.list(tenantId),
    enabled: open,
  });

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const toggle = (id: string) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    onChange(next);
  };

  return (
    <div className="relative inline-block" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="text-[11px] px-2 py-1 rounded-md transition-colors"
        style={{
          background: 'var(--bg-surface)',
          border: '1px solid var(--border)',
          color: 'var(--text-muted)',
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.background = 'var(--bg-hover)';
          e.currentTarget.style.color = 'var(--text)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.background = 'var(--bg-surface)';
          e.currentTarget.style.color = 'var(--text-muted)';
        }}
      >
        {t('vault_ui.tag_picker.add')}
      </button>
      {open && (
        <div
          className="absolute left-0 top-full mt-1.5 z-50 rounded-lg p-2 min-w-[220px] max-h-[300px] overflow-y-auto"
          style={{
            background: 'var(--bg-elevated)',
            border: '1px solid var(--border-strong)',
            boxShadow: 'var(--shadow-popover)',
          }}
        >
          {list.isLoading && (
            <p
              className="text-xs text-center py-2"
              style={{ color: 'var(--text-muted)' }}
            >
              {t('common.loading')}
            </p>
          )}
          {list.data && list.data.length === 0 && (
            <p
              className="text-xs text-center py-2"
              style={{ color: 'var(--text-muted)' }}
            >
              {t('vault_ui.tag_picker.empty')}
            </p>
          )}
          {list.data && list.data.length > 0 && (
            <div className="space-y-1">
              {list.data.map((tag) => {
                const active = selected.has(tag.id);
                return (
                  <button
                    key={tag.id}
                    type="button"
                    onClick={() => toggle(tag.id)}
                    className="w-full flex items-center justify-between gap-2 px-2 py-1.5 rounded transition-colors"
                    style={{
                      background: active ? 'var(--bg-hover)' : 'transparent',
                    }}
                    onMouseEnter={(e) => {
                      if (!active) e.currentTarget.style.background = 'var(--bg-hover)';
                    }}
                    onMouseLeave={(e) => {
                      if (!active) e.currentTarget.style.background = 'transparent';
                    }}
                  >
                    <TagPill label={tag.name} color={tag.color ?? 'neutral'} size="sm" />
                    <span
                      className="w-4 h-4 rounded grid place-items-center flex-shrink-0"
                      style={{
                        color: active ? 'var(--accent)' : 'var(--text-dim)',
                      }}
                    >
                      {active ? '✓' : ''}
                    </span>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
